<template>
  <AppLayout>

    <Head :title="board.name" />

    <div class="col-12 layout-spacing">
      <div v-if="board.department_id != null && !sameDepartment" class="alert alert-warning mb-3" role="status">
        <i class="bi bi-eye me-2"></i>
        <strong>Tryb tylko do odczytu.</strong>
        Możesz przeglądać zadania, ale nie możesz ich zmieniać,
        dodawać ani archiwizować.
      </div>

      <div class="widget widget-six">

        <div class="widget-heading">
          <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
            <div class="flex-grow-1 min-w-0">
              <Link href="/kanban/boards" class="text-sm text-blue-600 hover:underline">
              ← Lista tablic
              </Link>
              <h4 class="mt-3 mb-2">{{ board.name }}</h4>

              <p v-if="board.description" class="text-muted mb-0">
                {{ board.description }}
              </p>
            </div>
            <div class="board-actions d-flex align-items-center justify-content-lg-end gap-2 flex-shrink-0">
<Link
  v-if="this.isCreator || (sameDepartment && hasPermission('canViewActiveKanban'))"
  :href="`/kanban/boards/${board.id}/activity`"
  class="btn btn-outline-secondary btn-sm"
>
  <i class="bi bi-clock-history me-1"></i>
  Historia aktywności
</Link>

<Link
  v-if="this.isCreator || (sameDepartment && hasPermission('canViewActiveKanban'))"
  :href="`/kanban/boards/${board.id}/archived`"
  class="btn btn-outline-secondary btn-sm"
>
  <i class="bi bi-archive me-1"></i>
  Archiwum
</Link>

<button
  v-if="sameDepartment && hasPermission('canAddKanbanCard')"
  type="button"
  class="btn btn-primary me-5"
  @click="addZadania"
>
  <i class="bi bi-list-check me-1"></i>
  Gotowe zadania
</button>

            </div>
          </div>
        </div>
        <div class="widget-content">

          <div class="kanban-filter-toolbar">
            <div class="d-flex align-items-center gap-2">
              <button v-if="this.isCreator || (sameDepartment && hasPermission('canAddKanbanCard'))" type="button" class="btn btn-primary"
                @click="openCreateTaskModal">
                <i class="bi bi-plus-lg me-1"></i>
                Dodaj zadanie
              </button>
            </div>

            <div class="d-flex align-items-center gap-2">
              <button type="button" @click="filtersOpen = !filtersOpen" class="kanban-filter-toggle"
                :aria-expanded="filtersOpen">
                <i class="bi bi-sliders me-2"></i>
                {{ filtersOpen ? 'Ukryj filtry' : 'Filtry' }}
              </button>

              <button v-if="filtersOpen" type="button" @click="clearFilters" class="kanban-filter-clear">
                <i class="bi bi-x-circle me-2"></i>
                Wyczyść filtry
              </button>
            </div>
          </div>

        </div>
        <div class="kanban-filter-layout" :class="{ 'filters-open': filtersOpen }">
          <div class="kanban-filter-board">
            <div class="widget-content">
              <div class="kanban-board">
                <div class="kanban-columns">
                  <article v-for="column in filteredColumns" :key="column.id" class="kanban-column" :class="{
                    'kanban-column-drag-over':
                      sameDepartment && dragOverColumn === column.id
                  }" @dragover="sameDepartment && onDragOver(column, $event)"
                    @dragleave="sameDepartment && onDragLeave(column, $event)"
                    @drop="sameDepartment && onDrop(board, column, $event)">
                    <header class="kanban-column-header">
                      <div class="d-flex align-items-center gap-2">
                        <span class="kanban-column-color"
                          :style="{ backgroundColor: column.color || '#64748b' }"></span>
                        <h5 class="mb-0">{{ column.name }}</h5>
                      </div>
                      <span class="badge bg-light text-dark">
                        {{ column.cards.length }}
                      </span>
                    </header>

                    <!-- Karty -->
                    <div class="kanban-cards">
                      <article v-for="card in column.cards" :key="card.id" :draggable="sameDepartment"
                        class="kanban-card" :class="{
                          'kanban-card-moving': movingCardId === card.id,
                          'kanban-card-readonly': !sameDepartment
                        }" @dragstart="sameDepartment && onDragStart(card, $event)" @dragend="dragOverColumn = null"
                        @click="sameDepartment && openCard(card, board)">
                        <div class="d-flex align-items-start justify-content-between gap-2">
                          <div class="d-flex align-items-center gap-2 min-w-0">
                            <span v-if="card.project_id" class="kanban-project-number"
                              :title="`Projekt ${projects.find(p => Number(p.id) === Number(card.project_id))?.number ?? ''}`">
                              {{projects.find(p => Number(p.id) === Number(card.project_id))?.number ?? '?'}}
                            </span>

                            <h6 class="mb-0 fw-semibold">
                              {{ card.title }}
                            </h6>
                          </div>

                          <button v-if="sameDepartment && hasPermission('canArchiveKanbanCard')" type="button"
                            class="btn btn-sm btn-outline-danger kanban-card-archive" title="Archiwizuj kartę"
                            @click.stop="archiveCard(card)">
                            <i class="bi bi-archive"></i>
                          </button>
                        </div>

                        <p v-if="card.description" class="mt-2 mb-0 text-muted small kanban-card-description">
                          {{ card.description }}
                        </p>

                        <!-- Informacje o karcie -->
                        <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
                          <span v-if="Number(card.comments_count ?? card.comments?.length ?? 0) > 0"
                            class="kanban-card-info text-primary" title="Karta ma komentarze">
                            <i class="bi bi-chat-dots-fill"></i>
                            <span>{{ card.comments_count ?? card.comments.length }}</span>
                          </span>

                          <span v-if="isOverdue(card)" class="kanban-card-info kanban-overdue">
                            <i class="bi bi-exclamation-circle-fill"></i>
                            Przekroczono o {{ overdueDays(card) }}
                            {{ overdueDays(card) === 1 ? 'dzień' : 'dni' }}
                          </span>

                          <span v-if="card.updated_at" class="kanban-card-info kanban-card-edited"
                            :title="`Ostatnia edycja: ${relativeTime(card.updated_at)}`">
                            <i class="bi bi-pencil-square"></i>
                            {{ relativeTime(card.updated_at) }}
                          </span>
                        </div>

                        <div class="d-flex flex-wrap gap-2 mt-3">
                          <span class="badge" :class="{
                            'bg-secondary': card.priority === 'low',
                            'bg-primary': card.priority === 'normal',
                            'bg-warning text-dark': card.priority === 'high',
                            'bg-danger': card.priority === 'urgent'
                          }">
                            {{ priorityLabel(card.priority) }}
                          </span>

                          <span v-if="card.due_at" class="badge bg-light text-dark">
                            <i class="bi bi-calendar-event me-1"></i>
                            {{ formatDate(card.due_at) }}
                          </span>
                        </div>

                        <!-- Etykiety -->
                        <div v-if="card.labels?.length" class="kanban-card-labels mt-3">
                          <span v-for="label in card.labels" :key="label.id" class="kanban-card-label"
                            :style="{ backgroundColor: label.color || '#64748b' }">
                            <i v-if="label.icon" :class="label.icon" class="me-1" aria-hidden="true"></i>
                            {{ label.name }}
                          </span>
                        </div>

                        <!-- Osoby przypisane -->
                        <p v-if="card.assignees?.length" class="mt-3 mb-0 text-muted small">
                          <i class="bi bi-people me-1"></i>
                          {{card.assignees.map(user => user.name).join(', ')}}
                        </p>

                        <!-- Postęp wykonania -->
                        <div v-if="Number(card.percent) > 0" class="kanban-progress mt-3">
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <span class="small text-muted">Postęp</span>
                            <span class="small fw-semibold">
                              {{ Math.min(100, Math.max(0, Number(card.percent))) }}%
                            </span>
                          </div>

                          <div class="progress" role="progressbar"
                            :aria-valuenow="Math.min(100, Math.max(0, Number(card.percent)))" aria-valuemin="0"
                            aria-valuemax="100">
                            <div class="progress-bar" :style="{
                              width: `${Math.min(100, Math.max(0, Number(card.percent)))}%`
                            }"></div>
                          </div>
                        </div>
                      </article>

                      <div v-if="column.cards.length === 0" class="kanban-empty">
                        <i class="bi bi-inbox d-block mb-2"></i>
                        Brak kart
                      </div>
                    </div>

                  </article>
                </div>
              </div>
            </div>
          </div>

          <!-- PRAWA STRONA: FILTRY -->
          <aside v-if="filtersOpen" class="kanban-filter-sidebar">
            <div class="kanban-filter-panel">
              <div class="kanban-filter-panel-heading">
                <h5 class="mb-0">
                  <i class="bi bi-funnel me-2"></i>
                  Filtry zadań
                </h5>

                <button type="button" class="kanban-filter-close" aria-label="Zamknij filtry"
                  @click="filtersOpen = false">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>

              <div class="kanban-filter-field">
                <label for="filter-project">Projekt</label>
                <select id="filter-project" v-model="filters.project_id" class="form-select">
                  <option value="">Wszystkie projekty</option>
                  <option v-for="project in projects" :key="project.id" :value="String(project.id)">
                    {{ project.number }}
                  </option>
                </select>
              </div>

              <div class="kanban-filter-field">
                <label>Etykiety</label>
                <div class="kanban-filter-options">
                  <label v-for="label in board.labels" :key="label.id" class="kanban-filter-option">
                    <input type="checkbox" :value="label.id" v-model="filters.label_ids" />
                    <span>{{ label.name }}</span>
                  </label>

                  <p v-if="!board.labels?.length" class="text-muted small mb-0">
                    Brak etykiet.
                  </p>
                </div>
              </div>

              <div class="kanban-filter-field">
                <label>Przypisana osoba</label>
                <div class="kanban-filter-options">
                  <label v-for="person in availableUsers" :key="person.id" class="kanban-filter-option">
                    <input type="checkbox" :value="person.id" v-model="filters.user_ids" />
                    <span>{{ person.last_name }} {{ person.first_name }}</span>
                  </label>

                  <p v-if="!availableUsers.length" class="text-muted small mb-0">
                    Brak pracowników.
                  </p>
                </div>
              </div>

              <div class="kanban-filter-field">
                <label>Termin wykonania</label>
                <div class="kanban-filter-options">
                  <label v-for="option in [
                    { value: '', label: 'Dowolny termin' },
                    { value: 'overdue', label: 'Po terminie' },
                    { value: '24h', label: 'Najbliższe 24 godziny' },
                    { value: '7d', label: 'Najbliższe 7 dni' },
                    { value: '30d', label: 'Najbliższe 30 dni' },
                    { value: 'no_due_date', label: 'Bez terminu' }
                  ]" :key="option.value || 'any-date'" class="kanban-filter-option">
                    <input type="radio" name="due_date_filter" :value="option.value" v-model="filters.due_date" />
                    <span>{{ option.label }}</span>
                  </label>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>

    <!-- Modal dodawania nowego zadania -->
    <div v-if="createTaskModalOpen" class="kanban-modal-backdrop" @click.self="closeCreateTaskModal"
      @keydown.esc="closeCreateTaskModal">
      <section role="dialog" aria-modal="true" aria-labelledby="create-task-title"
        class="kanban-modal kanban-create-task-modal">
        <header class="kanban-modal-header">
          <div>
            <h5 id="create-task-title" class="mb-1">
              <i class="bi bi-plus-circle me-2"></i>
              Dodaj nowe zadanie
            </h5>
            <p class="text-muted small mb-0">
              Zadanie zostanie dodane do pierwszej kolumny tablicy.
            </p>
          </div>

          <button type="button" class="btn btn-light btn-sm" aria-label="Zamknij" @click="closeCreateTaskModal">
            <i class="bi bi-x-lg"></i>
          </button>
        </header>

        <div class="kanban-modal-body">
          <form @submit.prevent="createTask">
            <!-- Tytuł -->
            <div class="mb-3">
              <label for="new-task-title" class="form-label">
                Nazwa zadania *
              </label>
              <input id="new-task-title" v-model="newTaskForm.title" type="text" required maxlength="200" autofocus
                placeholder="Wpisz nazwę zadania" class="form-control"
                :class="{ 'is-invalid': newTaskForm.errors.title }" />
              <div v-if="newTaskForm.errors.title" class="invalid-feedback">
                {{ newTaskForm.errors.title }}
              </div>
            </div>

            <!-- Opis -->
            <div class="mb-3">
              <label for="new-task-description" class="form-label">
                Opis
              </label>
              <textarea id="new-task-description" v-model="newTaskForm.description" rows="4"
                placeholder="Opisz, co należy zrobić..." class="form-control"
                :class="{ 'is-invalid': newTaskForm.errors.description }"></textarea>
              <div v-if="newTaskForm.errors.description" class="invalid-feedback">
                {{ newTaskForm.errors.description }}
              </div>
            </div>

            <!-- Priorytet -->
            <div class="mb-3">
              <label for="new-task-priority" class="form-label">
                Priorytet
              </label>
              <select id="new-task-priority" v-model="newTaskForm.priority" class="form-select"
                :class="{ 'is-invalid': newTaskForm.errors.priority }">
                <option value="low">Niski</option>
                <option value="normal">Normalny</option>
                <option value="high">Wysoki</option>
                <option value="urgent">Pilny</option>
              </select>
              <div v-if="newTaskForm.errors.priority" class="invalid-feedback">
                {{ newTaskForm.errors.priority }}
              </div>
            </div>

            <!-- Projekt i pracownicy tylko dla tablicy shared -->
            <template v-if="board.visibility === 'shared'">
              <div class="mb-3">
                <label for="new-task-project" class="form-label">
                  Projekt
                </label>
                <select id="new-task-project" v-model="newTaskForm.project_id" class="form-select"
                  :class="{ 'is-invalid': newTaskForm.errors.project_id }">
                  <option :value="null">Bez przypisanego projektu</option>
                  <option v-for="project in projects" :key="project.id" :value="Number(project.id)">
                    {{ project.number }}
                  </option>
                </select>
                <div v-if="newTaskForm.errors.project_id" class="invalid-feedback">
                  {{ newTaskForm.errors.project_id }}
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">
                  Przypisani pracownicy
                </label>
                <Multiselect v-model="newCardAssignees" :options="availableUsersWithNames" :multiple="true"
                  :searchable="true" :close-on-select="false" :clear-on-select="false" :preserve-search="true"
                  label="full_name" track-by="id" placeholder="Wyszukaj pracowników..." select-label="Dodaj"
                  selected-label="Wybrano" deselect-label="Usuń" no-options="Brak dostępnych pracowników"
                  no-result="Nie znaleziono pracownika" />
                <div v-if="newTaskForm.errors.user_ids" class="text-danger small mt-2">
                  {{ newTaskForm.errors.user_ids }}
                </div>
                <div v-if="newTaskForm.errors['user_ids.0']" class="text-danger small mt-2">
                  {{ newTaskForm.errors['user_ids.0'] }}
                </div>
              </div>
            </template>

            <!-- Termin -->
            <div class="mb-4">
              <label for="new-task-due-date" class="form-label">
                Termin wykonania
              </label>
              <input id="new-task-due-date" v-model="newTaskForm.due_at" type="date" class="form-control"
                :class="{ 'is-invalid': newTaskForm.errors.due_at }" />
              <div v-if="newTaskForm.errors.due_at" class="invalid-feedback">
                {{ newTaskForm.errors.due_at }}
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2 border-top pt-3">
              <button type="button" class="btn btn-outline-secondary" :disabled="newTaskForm.processing"
                @click="closeCreateTaskModal">
                Anuluj
              </button>

              <button type="submit" class="btn btn-primary" :disabled="newTaskForm.processing">
                <i class="bi bi-plus-lg me-1"></i>
                {{
                  newTaskForm.processing
                    ? 'Dodawanie...'
                    : 'Dodaj zadanie'
                }}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>

    <!-- Modal edycji karty: dostępny tylko dla osób z tego samego działu -->
    <div v-if="editingCard && sameDepartment" class="kanban-modal-backdrop" @click.self="closeCard"
      @keydown.esc="closeCard">
      <section role="dialog" aria-modal="true" aria-labelledby="edit-card-title" class="kanban-modal">
        <header class="kanban-modal-header">
          <div>
            <h5 id="edit-card-title" class="mb-1">Edycja karty</h5>
            <p class="text-muted small mb-0">
              {{ editingCard.title }}
            </p>
          </div>

          <button type="button" class="btn btn-light btn-sm" aria-label="Zamknij" @click="closeCard">
            <i class="bi bi-x-lg"></i>
          </button>
        </header>

        <div class="kanban-modal-body">
          <!-- Dane karty -->
          <form @submit.prevent="saveCard(board.id)">
            <div class="mb-3">
              <label class="form-label">Tytuł</label>
              <input v-model="editForm.title" type="text" required maxlength="200" class="form-control"
                :class="{ 'is-invalid': editForm.errors.title }" />
              <div v-if="editForm.errors.title" class="invalid-feedback">
                {{ editForm.errors.title }}
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Opis</label>
              <textarea v-model="editForm.description" rows="5" class="form-control"
                :class="{ 'is-invalid': editForm.errors.description }"></textarea>
              <div v-if="editForm.errors.description" class="invalid-feedback">
                {{ editForm.errors.description }}
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Priorytet</label>
              <select v-model="editForm.priority" class="form-select">
                <option value="low">Niski</option>
                <option value="normal">Normalny</option>
                <option value="high">Wysoki</option>
                <option value="urgent">Pilny</option>
              </select>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Data rozpoczęcia</label>
                <input v-model="editForm.start_at" type="date" class="form-control"
                  :class="{ 'is-invalid': editForm.errors.start_at }" />
                <div v-if="editForm.errors.start_at" class="invalid-feedback">
                  {{ editForm.errors.start_at }}
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Termin wykonania</label>
                <input v-model="editForm.due_at" type="date" class="form-control"
                  :class="{ 'is-invalid': editForm.errors.due_at }" />
                <div v-if="editForm.errors.due_at" class="invalid-feedback">
                  {{ editForm.errors.due_at }}
                </div>
              </div>
            </div>

            <div v-if="(editingCard.assignees?.length || 0) > 0 || availableUsersWithNames.length > 0" class="mt-4">
              <label class="form-label fw-semibold">
                Pracownicy przypisani do karty
              </label>

              <div v-if="editingCard.assignees?.length" class="kanban-options-list mb-3">
                <div v-for="person in editingCard.assignees" :key="person.id" class="kanban-option">
                  <span class="flex-grow-1">
                    {{ person.full_name || person.name || [person.last_name, person.first_name].filter(Boolean).join(' ') }}
                  </span>

                  <button type="button" class="btn btn-sm btn-outline-danger" :disabled="assigneeForm.processing"
                    @click="removeAssignee(person)">
                    Usuń
                  </button>
                </div>
              </div>

              <p v-else class="text-muted small mb-3">
                Do tej karty nie przypisano jeszcze żadnej osoby.
              </p>

              <template v-if="availableUsersWithNames.length > 0">
                <label class="form-label">Dodaj pracownika</label>

                <Multiselect v-model="selectedAssigneeToAdd" :options="availableUsersWithNames.filter(
                  user => !(editingCard.assignees || []).some(
                    assigned => Number(assigned.id) === Number(user.id)
                  )
                )" :multiple="false" :searchable="true" :close-on-select="true" :clear-on-select="true"
                  :preserve-search="false" label="full_name" track-by="id" placeholder="Wyszukaj pracownika działu..."
                  select-label="Dodaj" selected-label="Wybrano" deselect-label="Usuń wybór"
                  no-options="Brak dostępnych pracowników" no-result="Nie znaleziono pracownika" />

                <button type="button" class="btn btn-primary mt-3"
                  :disabled="!selectedAssigneeToAdd || assigneeForm.processing" @click="addAssignee">
                  {{ assigneeForm.processing ? 'Zapisywanie...' : 'Dodaj osobę do karty' }}
                </button>

                <p v-if="assigneeForm.errors.user_ids" class="text-danger small mt-2">
                  {{ assigneeForm.errors.user_ids }}
                </p>
              </template>
            </div>

            <div class="mb-3">
              <label class="form-label">Projekt</label>
              <select v-model="editForm.project_id" class="form-select"
                :class="{ 'is-invalid': editForm.errors.project_id }">
                <option :value="null">Bez przypisanego projektu</option>
                <option v-for="project in projects" :key="project.id" :value="project.id">
                  {{ project.number }}
                </option>
              </select>

              <div v-if="editForm.errors.project_id" class="invalid-feedback">
                {{ editForm.errors.project_id }}
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Postęp wykonania</label>
              <div class="d-flex align-items-center gap-3">
                <input v-model.number="editForm.percent" type="range" min="0" max="100" step="5"
                  class="form-range flex-grow-1" />
                <span class="fw-semibold" style="min-width: 45px;">
                  {{ Number(editForm.percent) || 0 }}%
                </span>
              </div>

              <div class="progress mt-2" style="height: 8px;">
                <div class="progress-bar" role="progressbar" :style="{
                  width: `${Math.min(100, Math.max(0, Number(editForm.percent) || 0))}%`
                }" :aria-valuenow="Number(editForm.percent) || 0" aria-valuemin="0" aria-valuemax="100"></div>
              </div>

              <div v-if="editForm.errors.percent" class="text-danger small mt-1">
                {{ editForm.errors.percent }}
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2 border-bottom pb-4">
              <button type="button" class="btn btn-outline-secondary" @click="closeCard">
                Anuluj
              </button>

              <button type="submit" class="btn btn-primary" :disabled="editForm.processing">
                <i class="bi bi-save me-1"></i>
                {{ editForm.processing ? 'Zapisywanie...' : 'Zapisz zmiany' }}
              </button>
            </div>
          </form>

          <!-- Etykiety -->
          <section class="kanban-modal-section">
            <h6 class="mb-3">
              <i class="bi bi-tags me-2"></i>
              Etykiety
            </h6>

            <div v-if="board.labels?.length" class="kanban-options-list">
              <label v-for="label in board.labels" :key="label.id" class="kanban-option">
                <input v-model="cardLabelForm.label_ids" type="checkbox" :value="label.id"
                  class="form-check-input mt-0" />
                <span class="badge" :style="{ backgroundColor: label.color || '#64748b' }">
                  {{ label.name }}
                </span>
              </label>
            </div>

            <p v-else class="text-muted small">
              Nie utworzono jeszcze żadnych etykiet.
            </p>

            <p v-if="cardLabelForm.errors.label_ids" class="text-danger small mt-2">
              {{ cardLabelForm.errors.label_ids }}
            </p>

            <button type="button" class="btn btn-primary btn-sm mt-3" :disabled="cardLabelForm.processing"
              @click="saveCardLabels">
              <i class="bi bi-save me-1"></i>
              {{
                cardLabelForm.processing
                  ? 'Zapisywanie...'
                  : 'Zapisz etykiety karty'
              }}
            </button>

            <!-- Tworzenie etykiety -->
            <form class="kanban-subform" @submit.prevent="createLabel">
              <h6 class="mb-3">Utwórz nową etykietę</h6>

              <div class="mb-3">
                <label class="form-label">Nazwa etykiety</label>
                <input v-model="labelForm.name" type="text" required maxlength="50" placeholder="Nazwa etykiety"
                  class="form-control" :class="{ 'is-invalid': labelForm.errors.name }" />
                <div v-if="labelForm.errors.name" class="invalid-feedback">
                  {{ labelForm.errors.name }}
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Kolor</label>
                <div class="d-flex align-items-center gap-2">
                  <input v-model="labelForm.color" type="color" class="kanban-color-picker"
                    aria-label="Kolor etykiety" />
                </div>
              </div>

              <button type="submit" class="btn btn-outline-primary btn-sm" :disabled="labelForm.processing">
                <i class="bi bi-plus-circle me-1"></i>
                {{ labelForm.processing ? 'Tworzenie...' : 'Utwórz etykietę' }}
              </button>
            </form>
          </section>

          <!-- Załączniki -->
          <section class="kanban-modal-section">
            <h6 class="mb-3">
              <i class="bi bi-paperclip me-2"></i>
              Załączniki
            </h6>

            <form @submit.prevent="uploadAttachment">
              <div class="mb-3">
                <input type="file" class="form-control"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.webp"
                  @change="attachmentForm.file = $event.target.files?.[0] ?? null" />

                <div v-if="attachmentForm.errors.file" class="text-danger small mt-2">
                  {{ attachmentForm.errors.file }}
                </div>

                <div class="form-text">Maksymalny rozmiar pliku: 10 MB.</div>
              </div>

              <button type="submit" class="btn btn-primary btn-sm"
                :disabled="attachmentForm.processing || !attachmentForm.file">
                <i class="bi bi-upload me-1"></i>
                {{ attachmentForm.processing ? 'Przesyłanie...' : 'Dodaj plik' }}
              </button>
            </form>

            <div v-if="editingCard.attachments?.length" class="kanban-attachments mt-4">
              <div v-for="attachment in editingCard.attachments" :key="attachment.id" class="kanban-attachment">
                <div class="min-w-0">
                  <p class="mb-1 text-break fw-semibold small">
                    <i class="bi bi-file-earmark me-1"></i>
                    {{ attachment.original_name }}
                  </p>
                  <p class="mb-0 text-muted small">
                    {{ (attachment.size / 1024).toFixed(1) }} KB
                  </p>
                </div>

                <a :href="`/kanban/boards/${board.id}/cards/${editingCard.id}/attachments/${attachment.id}/download`"
                  class="btn btn-outline-primary btn-sm flex-shrink-0">
                  <i class="bi bi-download me-1"></i>
                  Pobierz
                </a>
              </div>
            </div>

            <p v-else class="text-muted small mt-3 mb-0">
              Brak załączników.
            </p>
          </section>

          <!-- Komentarze -->
          <section class="kanban-modal-section">
            <h6 class="mb-3">
              <i class="bi bi-chat-left-text me-2"></i>
              Komentarze
            </h6>

            <div v-if="editingCard.comments?.length" class="kanban-comments">
              <article v-for="comment in editingCard.comments" :key="comment.id" class="kanban-comment">
                <p class="mb-1 fw-semibold small">
                  {{ comment.user?.name ?? 'Użytkownik' }}
                </p>
                <p class="mb-0 text-break">{{ comment.body }}</p>
              </article>
            </div>

            <p v-else class="text-muted small">Brak komentarzy.</p>

            <form class="mt-3" @submit.prevent="addComment">
              <div class="mb-3">
                <textarea v-model="commentForm.body" rows="3" maxlength="5000" required
                  placeholder="Napisz komentarz..." class="form-control"
                  :class="{ 'is-invalid': commentForm.errors.body }"></textarea>
                <div v-if="commentForm.errors.body" class="invalid-feedback">
                  {{ commentForm.errors.body }}
                </div>
              </div>

              <button type="submit" class="btn btn-primary btn-sm"
                :disabled="commentForm.processing || !commentForm.body.trim()">
                <i class="bi bi-send me-1"></i>
                {{ commentForm.processing ? 'Dodawanie...' : 'Dodaj komentarz' }}
              </button>
            </form>
          </section>
        </div>
      </section>
    </div>
    <AddZadaniaModal ref="addZadaniaModalRef" :board="board" :sameDepartment="sameDepartment"
      :availableUsersWithNames="availableUsersWithNames" :projects="projects"
      @create-selected-tasks="createMultipleTasks" />
  </AppLayout>
</template>

<script>
import AppLayout from '@/Layouts/App.vue'
import { Head, Link, router, useForm } from '@inertiajs/vue3'
import { usePermission } from '@/composables/resources/js/composables/permissions.js'
import Multiselect from '@suadelabs/vue3-multiselect';
import AddZadaniaModal from "./AddZadaniaModal.vue";

const { hasPermission } = usePermission()

export default {
  components: {
    AppLayout,
    AddZadaniaModal,
    Head,
    Link,
    Multiselect,
  },

  props: {
    projects: {
      type: Array,
      default: () => [],
    },
    board: {
      type: Object,
      required: true,
    },
    boardId: Number,
    isCreator: {
      type: Boolean,
      default: false,
    },
    hasProjectFiles: {
      type: Boolean,
      default: false,
    },
    availableUsers: {
      type: Array,
      default: () => [],
    },
    sameDepartment: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      editingCard: null,

      editingBoardId: null,
      hasPermission,
      draggedCard: null,
      dragOverColumn: null,

      // Nowy modal dodawania zadania
      createTaskModalOpen: false,

      // Pracownicy przypisani do nowego zadania
      newCardAssignees: [],

      movingCardId: null,
      selectedAssigneeToAdd: null,
      selectedAssignees: [],

      filtersOpen: false,
      filters: {
        project_id: '',
        label_ids: [],
        user_ids: [],
        due_date: '',
      },

      // Formularz nowego zadania
      newTaskForm: useForm({
        title: '',
        description: '',
        priority: 'normal',
        due_at: '',
        project_id: null,
        user_ids: [],
      }),

      // Formularz edycji istniejącej karty
      editForm: useForm({
        title: '',
        description: '',
        priority: 'normal',
        start_at: '',
        due_at: '',
        project_id: null,
        percent: 0,
      }),

      assigneeForm: useForm({
        user_ids: [],
      }),

      commentForm: useForm({
        body: '',
      }),

      labelForm: useForm({
        name: '',
        color: '#64748b',
      }),

      cardLabelForm: useForm({
        label_ids: [],
      }),

      attachmentForm: useForm({
        file: null,
      }),
    }
  },

  computed: {

    availableUsersWithNames() {
      const usersById = new Map()

      for (const user of this.availableUsers) {
        const id = Number(user.id)

        usersById.set(id, {
          ...user,
          id,
          full_name: `${user.last_name ?? ''} ${user.first_name ?? ''}`.trim()
            || user.name
            || `Użytkownik ${id}`,
        })
      }

      // Zachowaj przypisanych użytkowników, nawet jeśli backend
      // nie zwrócił ich na liście dostępnych opcji.
      for (const user of this.editingCard?.assignees ?? []) {
        const id = Number(user.id)

        if (!usersById.has(id)) {
          usersById.set(id, {
            ...user,
            id,
            full_name:
              user.full_name
              || `${user.last_name ?? ''} ${user.first_name ?? ''}`.trim()
              || user.name
              || `Użytkownik ${id}`,
          })
        }
      }

      return [...usersById.values()].sort((a, b) =>
        a.full_name.localeCompare(b.full_name, 'pl')
      )
    },

    filteredColumns() {
      const now = new Date()

      return this.board.columns.map(column => ({
        ...column,
        cards: column.cards.filter(card => {
          if (
            this.filters.project_id !== '' &&
            String(card.project_id ?? '') !== String(this.filters.project_id)
          ) {
            return false
          }

          if (this.filters.label_ids.length) {
            const cardLabelIds = (card.labels || []).map(
              label => Number(label.id)
            )

            if (
              !this.filters.label_ids.some(id =>
                cardLabelIds.includes(Number(id))
              )
            ) {
              return false
            }
          }

          if (this.filters.user_ids.length) {
            const cardUserIds = (card.assignees || []).map(
              user => Number(user.id)
            )

            if (
              !this.filters.user_ids.some(id =>
                cardUserIds.includes(Number(id))
              )
            ) {
              return false
            }
          }

          if (this.filters.due_date) {
            if (!card.due_at) {
              if (this.filters.due_date !== 'no_due_date') {
                return false
              }
            } else {
              const due = new Date(card.due_at)
              const hours = (due - now) / (1000 * 60 * 60)

              switch (this.filters.due_date) {
                case 'overdue':
                  if (!this.isOverdue(card)) return false
                  break

                case '24h':
                  if (!(hours >= 0 && hours <= 24)) return false
                  break

                case '7d':
                  if (!(hours >= 0 && hours <= 24 * 7)) return false
                  break

                case '30d':
                  if (!(hours >= 0 && hours <= 24 * 30)) return false
                  break

                case 'no_due_date':
                  return false
              }
            }
          }

          return true
        }),
      }))
    },
  },

  methods: {

    clearFilters() {
      this.filters = {
        project_id: '',
        label_ids: [],
        user_ids: [],
        due_date: '',
      };
    },

    openCreateTaskModal() {
      if (!this.sameDepartment || !this.hasPermission('canAddKanbanCard')) {
        return;
      }

      if (!this.board.columns?.length) {
        this.errorMsg('Tablica nie ma żadnej kolumny.');
        return;
      }

      this.newTaskForm.reset();
      this.newTaskForm.clearErrors();

      this.newTaskForm.priority = 'normal';
      this.newTaskForm.project_id = null;
      this.newTaskForm.due_at = '';
      this.newTaskForm.user_ids = [];

      this.newCardAssignees = [];
      this.createTaskModalOpen = true;
    },

    closeCreateTaskModal() {
      if (this.newTaskForm.processing) {
        return;
      }

      this.createTaskModalOpen = false;
      this.newTaskForm.reset();
      this.newTaskForm.clearErrors();
      this.newCardAssignees = [];
    },

    createTask() {
      if (!this.sameDepartment || !this.hasPermission('canAddKanbanCard')) {
        return;
      }

      // Nowe zadanie zawsze trafia do pierwszej kolumny.
      const firstColumn = this.board.columns?.[0];

      if (!firstColumn?.id) {
        this.errorMsg('Nie znaleziono pierwszej kolumny tablicy.');
        return;
      }

      if (!this.newTaskForm.title?.trim()) {
        this.newTaskForm.setError(
          'title',
          'Nazwa zadania jest wymagana.'
        );
        return;
      }

      this.newTaskForm.clearErrors();
      this.newTaskForm.title = this.newTaskForm.title.trim();

      // Projekty i pracownicy tylko na tablicy współdzielonej.
      if (this.board.visibility === 'shared') {
        this.newTaskForm.user_ids = [
          ...new Set(
            this.newCardAssignees.map(user => Number(user.id))
          ),
        ];

        this.newTaskForm.project_id =
          this.newTaskForm.project_id !== null &&
            this.newTaskForm.project_id !== ''
            ? Number(this.newTaskForm.project_id)
            : null;
      } else {
        this.newTaskForm.user_ids = [];
        this.newTaskForm.project_id = null;
      }

      this.newTaskForm.post(
        `/kanban/boards/${this.board.id}/columns/${firstColumn.id}/cards`,
        {
          preserveScroll: true,

          onSuccess: () => {
            this.createTaskModalOpen = false;

            this.newTaskForm.reset();
            this.newTaskForm.clearErrors();
            this.newCardAssignees = [];

            this.successMsg(
              'Zadanie zostało dodane do pierwszej kolumny.'
            );
          },

          onError: (errors) => {
            this.errorMsg(
              Object.values(errors).flat().join('\n') ||
              'Nie udało się dodać zadania.'
            );
          },
        }
      );
    },

    async createMultipleTasks({ tasks, userIds, projectId = null }) {
      if (!this.sameDepartment || !this.board?.id) return;
      if (!tasks?.length || !userIds?.length) {
        this.errorMsg('Wybierz zadania i użytkowników.');
        return;
      }

      const modal = this.$refs.addZadaniaModalRef;
      modal?.setBulkCreating(true);

      try {
        const columnId = this.board.columns?.[0]?.id;

        if (!columnId) {
          throw new Error('Nie znaleziono kolumny do utworzenia kart.');
        }

        const response = await axios.post(
          `/kanban/boards/${this.board.id}/columns/${columnId}/cards/bulk`,
          {
            tasks: tasks.map(task => ({
              title: task.title,
              description: task.description || '',
            })),
            user_ids: [...new Set(userIds.map(Number))],
            project_id: projectId === null || projectId === ''
              ? null
              : Number(projectId),
          },
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          }
        );

        const createdCount = response.data?.created_count ?? 0;

        modal?.clearBulkSelection();
        modal?.hide();

        router.reload({
          preserveScroll: true,
          onSuccess: () => {
            this.successMsg(`Pomyślnie utworzono ${createdCount} kart.`);
          },
          onError: errors => {
            this.errorMsg(
              Object.values(errors).flat().join('\n') ||
              'Nie udało się odświeżyć tablicy.'
            );
          },
        });
      } catch (error) {
        const messages = error.response?.data?.errors;
        const message = messages
          ? Object.values(messages).flat().join('\n')
          : error.response?.data?.message || error.message ||
          'Nie udało się utworzyć kart.';

        this.errorMsg(message);
      } finally {
        modal?.setBulkCreating(false);
      }
    },

    addZadania() {
      this.$refs.addZadaniaModalRef?.show();
    },
    getColumnPosition(card) {
      const column = this.board.columns.find(
        column => Number(column.id) === Number(card.board_column_id)
      )

      return column ? Number(column.position) : null
    },

    addAssignee() {
      if (!this.sameDepartment || !this.editingCard || !this.selectedAssigneeToAdd) {
        return;
      }

      const existingIds = (this.editingCard.assignees || [])
        .map(user => Number(user.id));

      const userId = Number(this.selectedAssigneeToAdd.id);

      if (existingIds.includes(userId)) {
        this.selectedAssigneeToAdd = null;
        return;
      }

      this.assigneeForm.user_ids = [...existingIds, userId];

      this.assigneeForm.put(
        `/kanban/boards/${this.board.id}/cards/${this.editingCard.id}/assignees`,
        {
          preserveScroll: true,
          onSuccess: page => {
            this.selectedAssigneeToAdd = null;
            this.refreshEditingCard(page);
            this.successMsg('Użytkownik został przypisany do karty.');
          },
          onError: errors => {
            this.errorMsg(Object.values(errors).flat().join('\n') ||
              'Nie udało się przypisać użytkownika.');
          },
        }
      );
    },


    removeAssignee(person) {
      if (!this.sameDepartment || !this.editingCard || !person) return;

      const remainingIds = (this.editingCard.assignees || [])
        .filter(user => Number(user.id) !== Number(person.id))
        .map(user => Number(user.id));

      this.assigneeForm.user_ids = remainingIds;

      this.assigneeForm.put(
        `/kanban/boards/${this.board.id}/cards/${this.editingCard.id}/assignees`,
        {
          preserveScroll: true,
          onSuccess: page => {
            this.refreshEditingCard(page);
            this.successMsg('Użytkownik został usunięty z karty.');
          },
          onError: errors => {
            this.errorMsg(Object.values(errors).flat().join('\n') ||
              'Nie udało się usunąć użytkownika.');
          },
        }
      );
    },



    isOverdue(card) {
      if (!card.due_at || this.getColumnPosition(card) === 3) {
        return false
      }

      const dueDate = new Date(card.due_at)
      if (Number.isNaN(dueDate.getTime())) return false

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      dueDate.setHours(0, 0, 0, 0)

      return dueDate < today
    },

    overdueDays(card) {
      if (!card.due_at) return 0
      const dueDate = new Date(card.due_at)
      const today = new Date()
      dueDate.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)
      return Math.max(
        0,
        Math.floor((today - dueDate) / 86400000)
      )
    },

    relativeTime(value) {
      if (!value) return ''

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) return ''

      const days = Math.floor(
        (Date.now() - date.getTime()) / 86400000
      )

      if (days < 0) return 'przed chwilą'
      if (days === 0) return 'dzisiaj'
      if (days === 1) return '1 dzień temu'
      if (days < 7) return `${days} dni temu`
      if (days < 14) return 'tydzień temu'
      if (days < 30) return `${Math.floor(days / 7)} tyg. temu`
      if (days < 60) return 'miesiąc temu'
      if (days < 365) return `${Math.floor(days / 30)} mies. temu`
      if (days < 730) return 'ponad rok temu'

      return `${Math.floor(days / 365)} lata temu`
    },

    toLocalDate(value) {
      if (!value) return ''
      return String(value).slice(0, 10)
    },

    openCard(card, board) {
      if (!this.sameDepartment) return

      this.editingBoardId = board.id
      this.editingCard = card
      this.selectedAssigneeToAdd = null;
      this.editForm.title = card.title ?? ''
      this.editForm.description = card.description ?? ''
      this.editForm.priority = card.priority ?? 'normal'
      this.editForm.start_at = card.start_at ? String(card.start_at).slice(0, 10) : '';
      this.editForm.due_at = card.due_at ? String(card.due_at).slice(0, 10) : ''
      this.editForm.project_id = card.project_id ? Number(card.project_id) : null
      this.editForm.percent = Number(card.percent) || 0
      this.editForm.clearErrors()

      this.assigneeForm.user_ids = (card.assignees ?? []).map(user => user.id)
      this.assigneeForm.clearErrors()

      this.cardLabelForm.label_ids = (card.labels ?? []).map(label => label.id)
      this.cardLabelForm.clearErrors()

      this.commentForm.reset()
      this.commentForm.clearErrors()

      this.attachmentForm.reset()
      this.attachmentForm.clearErrors()
    },

    closeCard() {
      this.editingCard = null;
      this.editingBoardId = null;
      this.selectedAssigneeToAdd = null;

      this.editForm.clearErrors();
      this.assigneeForm.clearErrors();
      this.cardLabelForm.clearErrors();
      this.commentForm.clearErrors();
      this.labelForm.clearErrors();
      this.attachmentForm.clearErrors();
    },


    saveCard(boardId) {
      if (!this.sameDepartment || !this.editingCard) return;

      this.editForm.patch(
        `/kanban/boards/${boardId}/cards/${this.editingCard.id}`,
        {
          preserveScroll: true,
          onSuccess: () => {
            this.closeCard();
            this.successMsg('Karta została zapisana.');
          },
          onError: errors => {
            this.errorMsg(Object.values(errors).flat().join('\n') ||
              'Nie udało się zapisać karty.');
          },
        }
      );
    },

    saveCardLabels() {
      if (!this.sameDepartment || !this.editingCard) return;

      this.cardLabelForm.put(
        `/kanban/boards/${this.board.id}/cards/${this.editingCard.id}/labels`,
        {
          preserveScroll: true,
          onSuccess: page => {
            this.refreshEditingCard(page);
            this.successMsg('Etykiety zostały zapisane.');
          },
          onError: errors => {
            this.errorMsg(Object.values(errors).flat().join('\n') ||
              'Nie udało się zapisać etykiet.');
          },
        }
      );
    },

    addComment() {
      if (
        !this.sameDepartment ||
        !this.editingCard ||
        !this.commentForm.body.trim()
      ) return;

      this.commentForm.body = this.commentForm.body.trim();

      this.commentForm.post(
        `/kanban/boards/${this.editingBoardId}/cards/${this.editingCard.id}/comments`,
        {
          preserveScroll: true,
          onSuccess: page => {
            this.commentForm.reset();
            this.refreshEditingCard(page);
            this.successMsg('Komentarz został dodany.');
          },
          onError: errors => {
            this.errorMsg(Object.values(errors).flat().join('\n') ||
              'Nie udało się dodać komentarza.');
          },
        }
      );
    },

    onDragStart(card, event) {
      if (!this.sameDepartment) return

      this.draggedCard = card
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(card.id))
    },

    onDragOver(column, event) {
      if (!this.sameDepartment) return

      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      this.dragOverColumn = column.id
    },

    onDragLeave(column, event) {
      if (!this.sameDepartment) return

      if (!event.currentTarget.contains(event.relatedTarget)) {
        if (this.dragOverColumn === column.id) {
          this.dragOverColumn = null
        }
      }
    },

    onDrop(board, column, event) {
      if (!this.sameDepartment) return

      event.preventDefault()

      const card = this.draggedCard

      this.dragOverColumn = null
      this.draggedCard = null

      if (!card || this.movingCardId !== null) return

      const sourceColumn = board.columns.find(item =>
        item.cards.some(existing => existing.id === card.id)
      )

      if (!sourceColumn) return

      const position = 1

      if (
        sourceColumn.id === column.id &&
        sourceColumn.cards[0]?.id === card.id
      ) {
        return
      }

      this.movingCardId = card.id


      router.patch(
        `/kanban/boards/${board.id}/cards/${card.id}/move`,
        {
          column_id: column.id,
          position,
        },
        {
          preserveScroll: true,
          onSuccess: () => {
            this.successMsg('Karta została przeniesiona.');
          },
          onError: errors => {
            this.errorMsg(Object.values(errors).flat().join('\n') ||
              'Nie udało się przenieść karty.');
          },
          onFinish: () => {
            this.movingCardId = null;
          },
        }
      );

    },

    createCard(boardId, columnId, event) {
      if (!this.sameDepartment) return;

      const formElement = event.currentTarget;
      const formData = new FormData(formElement);
      const data = Object.fromEntries(formData.entries());

      if (this.board.visibility === 'shared') {
        data.project_id = data.project_id
          ? Number(data.project_id)
          : null;

        data.user_ids = (this.newCardAssignees[columnId] || [])
          .map(user => Number(user.id));
      } else {
        delete data.project_id;
        data.user_ids = [];
      }

      router.post(
        `/kanban/boards/${boardId}/columns/${columnId}/cards`,
        data,
        {
          preserveScroll: true,

          onSuccess: () => {
            formElement.reset();
            this.newCardAssignees[columnId] = [];
            this.successMsg('Karta została utworzona.');
          },

          onError: errors => {
            this.errorMsg(
              Object.values(errors).flat().join('\n') ||
              'Nie udało się utworzyć karty.'
            );
          },
        }
      );
    },

    priorityLabel(priority) {
      return {
        low: 'Niski',
        normal: 'Normalny',
        high: 'Wysoki',
        urgent: 'Pilny',
      }[priority] ?? priority
    },

    formatDate(value) {
      if (!value) return null

      return new Intl.DateTimeFormat('pl-PL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(new Date(value))
    },

    createLabel() {
      if (!this.sameDepartment) return;

      this.labelForm.post(
        `/kanban/boards/${this.board.id}/labels`,
        {
          preserveScroll: true,
          onSuccess: page => {
            this.labelForm.reset('name');
            this.refreshEditingCard(page);
            this.successMsg('Etykieta została utworzona.');
          },
          onError: errors => {
            this.errorMsg(Object.values(errors).flat().join('\n') ||
              'Nie udało się utworzyć etykiety.');
          },
        }
      );
    },

    archiveCard(card) {
      if (!this.sameDepartment || !card) return;

      Toast.fire({
        toast: false,
        icon: 'warning',
        title: 'Archiwizacja karty',
        text: 'Czy na pewno chcesz zarchiwizować tę kartę?',
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: 'Tak',
        denyButtonText: 'Nie',
        timer: false,
        position: 'center',
      }).then(result => {
        if (!result.isConfirmed) return;

        router.patch(
          `/kanban/boards/${this.board.id}/cards/${card.id}/archive`,
          {},
          {
            preserveScroll: true,
            onSuccess: () => this.successMsg('Karta została zarchiwizowana.'),
            onError: errors => {
              this.errorMsg(Object.values(errors).flat().join('\n') ||
                'Nie udało się zarchiwizować karty.');
            },
          }
        );
      });
    },


    archiveBoard() {
      if (!this.sameDepartment) return;

      Toast.fire({
        toast: false,
        icon: 'warning',
        title: 'Archiwizacja tablicy',
        text: 'Czy na pewno chcesz zarchiwizować tę tablicę?',
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: 'Tak',
        denyButtonText: 'Nie',
        timer: false,
        position: 'center',
      }).then(result => {
        if (!result.isConfirmed) return;

        router.patch(
          `/kanban/boards/${this.board.id}/archive`,
          {},
          {
            preserveScroll: true,
            onSuccess: () => this.successMsg('Tablica została zarchiwizowana.'),
            onError: errors => {
              this.errorMsg(Object.values(errors).flat().join('\n') ||
                'Nie udało się zarchiwizować tablicy.');
            },
          }
        );
      });
    },

    successMsg(resp) {
      Toast.fire({
        position: 'top-end',
        toast: true,
        icon: 'success',
        title: 'Sukces!',
        html: resp,
        showClass: { popup: 'animate__animated animate__fadeInDown' },
        hideClass: { popup: 'animate__animated animate__fadeOutUp' },
        timer: 2500,
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true
      });
    },

    errorMsg(resp) {
      Toast.fire({
        position: 'top-end',
        toast: true,
        icon: 'warning',
        title: 'Błąd!',
        html: resp,
        showClass: { popup: 'animate__animated animate__fadeInDown' },
        hideClass: { popup: 'animate__animated animate__fadeOutUp' },
        timer: 2500,
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true
      });
    },

    uploadAttachment() {
      if (!this.sameDepartment) return;

      const card = this.editingCard;
      if (!card || !this.attachmentForm.file) return;

      const cardId = card.id;

      this.attachmentForm.post(
        `/kanban/boards/${this.board.id}/cards/${cardId}/attachments`,
        {
          forceFormData: true,
          preserveScroll: true,
          onSuccess: page => {
            this.attachmentForm.reset();
            this.refreshEditingCard(page, cardId);
            this.successMsg('Załącznik został dodany.');
          },
          onError: errors => {
            this.errorMsg(Object.values(errors).flat().join('\n') ||
              'Nie udało się dodać załącznika.');
          },
        }
      );
    },

    refreshEditingCard(page, cardId = this.editingCard?.id) {
      if (!cardId || !page.props.board?.columns) return

      const columns = page.props.board.columns

      for (const column of columns) {
        const updatedCard = column.cards?.find(
          item => Number(item.id) === Number(cardId)
        )

        if (updatedCard) {
          this.editingCard = updatedCard
          return
        }
      }
    },
  },
}
</script>

<style scoped>
.kanban-board {
  width: 100%;
  overflow-x: auto;
  padding-bottom: 1rem;
}

.kanban-columns {
  display: flex;
  align-items: stretch;
  gap: 1rem;
  width: 100%;
  min-width: 0;
}

.kanban-column {
  flex: 1 1 0;
  width: auto;
  min-width: 280px;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f8fafc;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.kanban-column-drag-over {
  background: #e7f1ff;
  border-color: #86b7fe;
}

.kanban-column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.kanban-column-header h5 {
  font-size: 0.95rem;
  font-weight: 600;
}

.kanban-column-color {
  display: inline-block;
  width: 11px;
  height: 11px;
  flex-shrink: 0;
  border-radius: 50%;
}

.kanban-cards {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 60px;
}

.kanban-card {
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 3px rgb(15 23 42 / 5%);
  cursor: grab;
  transition: box-shadow 0.15s ease, opacity 0.15s ease;
}

.kanban-card-readonly {
  cursor: default;
}

.kanban-card:hover {
  box-shadow: 0 4px 12px rgb(15 23 42 / 8%);
}

.kanban-card:active {
  cursor: grabbing;
}

.kanban-card-readonly:active {
  cursor: default;
}

.kanban-card-moving {
  opacity: 0.45;
}

.kanban-card-description {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.kanban-card-archive {
  flex-shrink: 0;
  padding: 0.15rem 0.4rem;
}

.kanban-empty {
  padding: 1.5rem 0.75rem;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #94a3b8;
  text-align: center;
  font-size: 0.85rem;
}

.kanban-empty i {
  font-size: 1.5rem;
}

.kanban-create-card {
  margin-top: 1rem;
  padding: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}

.kanban-create-card h6 {
  font-size: 0.875rem;
  font-weight: 600;
}

.kanban-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
  background: rgb(15 23 42 / 55%);
}

.kanban-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 760px;
  max-height: 92vh;
  overflow: hidden;
  border: 1px solid rgb(255 255 255 / 20%);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 20px 60px rgb(15 23 42 / 25%);
}

.kanban-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.kanban-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.kanban-modal-section {
  padding: 1.5rem 0;
  border-bottom: 1px solid #e9ecef;
}

.kanban-modal-section:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.kanban-modal-section h6 {
  font-weight: 600;
}

.kanban-options-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.kanban-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  overflow-wrap: anywhere;
}

.kanban-option:hover {
  background: #f8fafc;
}

.kanban-subform {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e9ecef;
}

.kanban-attachments {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.kanban-attachment {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.kanban-comments {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.kanban-comment {
  padding: 0.875rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f8fafc;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

@media (max-width: 575.98px) {
  .kanban-column {
    flex-basis: 280px;
    width: 280px;
    min-width: 280px;
  }

  .kanban-modal-backdrop {
    align-items: flex-start;
    padding: 0.5rem;
  }

  .kanban-modal {
    max-height: 96vh;
  }

  .kanban-modal-header,
  .kanban-modal-body {
    padding: 1rem;
  }

  .kanban-attachment {
    align-items: flex-start;
  }
}

.kanban-color-picker {
  display: block;
  width: 48px;
  height: 40px;
  padding: 3px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: transparent;
  cursor: pointer;
  appearance: auto;
  -webkit-appearance: auto;
}

.kanban-project-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 28px;
  height: 28px;
  padding: 0 4px;
  border-radius: 5px;
  background-color: #ffc107;
  color: #212529;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.kanban-card-info {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  line-height: 1.4;
}

.kanban-overdue {
  color: #dc3545;
  font-weight: 700;
}

.kanban-card-edited {
  color: #64748b;
}

.kanban-card-labels {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.4rem;
}

.kanban-card-label {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  color: #fff;
  font-size: 0.75rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.kanban-progress .progress {
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background-color: #e9ecef;
}

.kanban-progress .progress-bar {
  border-radius: 999px;
  transition: width 0.2s ease;
}

/* Pasek sterowania filtrami */
.kanban-filter-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin: 1.25rem 0;
}

.kanban-filter-toggle,
.kanban-filter-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1rem;
  border-radius: 9px;
  font-size: 0.875rem;
  font-weight: 600;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.kanban-filter-toggle {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
}

.kanban-filter-toggle:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.kanban-filter-clear {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
}

.kanban-filter-clear:hover {
  background: #e2e8f0;
  color: #334155;
}

/* Tablica i panel filtrów */
.kanban-filter-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.kanban-filter-layout.filters-open {
  grid-template-columns: minmax(0, 1fr) 290px;
}

.kanban-filter-board {
  min-width: 0;
}

.kanban-filter-board .widget-content {
  min-width: 0;
}

.kanban-filter-sidebar {
  min-width: 0;
  align-self: stretch;
}

.kanban-filter-panel {
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  padding: 1rem;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 24px rgb(15 23 42 / 6%);
}

.kanban-filter-panel-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #e2e8f0;
}

.kanban-filter-panel-heading h5 {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.kanban-filter-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #fff;
  color: #64748b;
}

.kanban-filter-close:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.kanban-filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-width: 0;
}

.kanban-filter-field>label {
  margin: 0;
  color: #334155;
  font-size: 0.85rem;
  font-weight: 700;
}

.kanban-filter-field .form-select {
  width: 100%;
  min-width: 0;
  border-color: #cbd5e1;
  border-radius: 8px;
  font-size: 0.85rem;
}

.kanban-filter-options {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-height: 180px;
  overflow-y: auto;
  padding: 0.1rem 0.15rem;
}

.kanban-filter-option {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin: 0;
  color: #475569;
  font-size: 0.84rem;
  line-height: 1.4;
  cursor: pointer;
  overflow-wrap: anywhere;
}

.kanban-filter-option input {
  flex-shrink: 0;
  margin-top: 0.15rem;
  accent-color: #2563eb;
}

@media (max-width: 991.98px) {
  .kanban-filter-layout.filters-open {
    grid-template-columns: minmax(0, 1fr) 260px;
    gap: 0.75rem;
  }

  .kanban-filter-panel {
    padding: 0.85rem;
  }
}

@media (max-width: 767.98px) {
  .kanban-filter-layout.filters-open {
    grid-template-columns: minmax(0, 1fr);
  }

  .kanban-filter-sidebar {
    grid-row: 1;
  }

  .kanban-filter-panel {
    position: static;
    max-height: none;
  }

  .kanban-filter-board {
    grid-row: 2;
  }
}
</style>
